import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { TreeView } from "devextreme-react";
import CheckBox from "devextreme/ui/check_box";
import RadioGroup from "devextreme/ui/radio_group";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ItemClickEvent, Node, SelectionChangedEvent } from "devextreme/ui/tree_view";
import { Autocomplete, Backdrop, Button, CircularProgress, TextField, Tooltip } from "@mui/material";
import { FilesInspection, MoveHighQFiles, MoveIManageFiles } from "../../api-client-nswag/taxportal-client";
import { ModalCopyFiles } from "../../cui/components/ModalCopyFiles/ModalCopyFiles";
import { Tree } from "../../cui/components/TreeView/TreeView";
import { SnackbarMessage } from "../../hooks/useSnackbar.types";
import { useContainerDimensions, useSnackbar } from "../../hooks";
import { taxPortalClientApi, fileManagementClientApi, createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { IsFileManagementMicroserviceEnabled } from "../../envVariables";
import {
  IManageComponentProps,
  IManageFilesDTO,
  IManageFilesVars,
  IManageEntityItem,
  iManageProps,
  taxPortalProps,
} from "./IManageFilesTypes";
import { OidcIdentityContext } from "@rsmus/react-auth";
import "./IManageFiles.scss";
import IManageFilesDialog from "../../cui/components/IManageFiles/IManageFilesDialog";
import ForwardIcon from "@mui/icons-material/Forward";
import { Typography, Divider, Box } from "@mui/material";
import {
  IMANAGEFILE_SWAP_TITLE,
  IMANAGEFILE_TITLE,
  IMANAGECSFILE_TITLE,
  IMANAGECSFILE_SWAP_TITLE,
  TAXPORTAL_ID,
  CORPORATESIGHT_ID,
  FILES_LIMIT_PLACEHOLDER,
} from "../../constants";
import { getFilePath, getFolderPath, findHighQEntity } from "./IManageTaxPortalHelper";
import { Descriptions } from "../../api-client-nswag/taxportal-client";
import { REACT_APP_IManage_FileCount_Limit } from "../../envVariables";
import { DialogBox } from "../CreateSites/DialogBox";
import { CleanHands } from "@mui/icons-material";

const { CS_FOLDERS, TAXPORTAL_FOLDERS, IMANAGE_FILES } = Descriptions;

type TreeViewRefType = TreeView;

export default function IManageFiles({
  onItemExpanded,
  showMoreLabel,
  filesToMove,
  foldersToShow,
  siteID,
  onSourceBoolChange,
  refresh,
  entities,
  expandedWorkspaceEntityId,
  onWorkspaceExpanded,
}: IManageComponentProps) {
  const componentRef = useRef<HTMLDivElement | null>(null);
  const { width } = useContainerDimensions(componentRef);
  const { showSnackbar } = useSnackbar();
  const { user } = useContext(OidcIdentityContext);
  const iManageTreeRef = useRef<TreeViewRefType>(null);
  const taxPortalTreeRef = useRef<TreeViewRefType>(null);
  const [isDisplayOverlay, setIsDisplayOverlay] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Node<any>[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<Node<any>[]>([]);
  const [selectedFoldersIManage, setSelectedFoldersIManage] = useState<Node<any>[]>([]);
  const [filesInspection, setFilesInspection] = useState<FilesInspection>({
    all: [],
    duplicated: [],
  });
  const [itemEdited, setItemEdited] = useState<Array<string>>([]);
  const [isDisplayModal, setIsDisplayModal] = useState(false);
  const [isBlurEnabled, setIsBlurEnabled] = useState(false);
  const isOneSelected = !!selectedFoldersIManage.length || !!selectedFolder.length;
  const [isSwap, setisSwap] = useState(false);
  const abortController = new AbortController();
  const [open, setOpen] = useState(false);
  const [iManageSource, setiManageSource] = useState(false);
  const [isCorporateSight, setIsCorporateSight] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [unprocessableErrorMessage, setUnprocessableErrorMessage] = useState<string>("");
  const [isUnprocessableDialogOpen, setIsUnprocessableDialogOpen] = useState(false);
  const unprocessableDialogCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { showSuccessSnackbar } = useSnackbar();
  const [selectedEntity, setSelectedEntity] = useState<IManageEntityItem | null>(null);

  const [isRefreshingImanage, setIsRefreshingImanage] = useState(false);
  const [isRefreshingTaxPortal, setIsRefreshingTaxPortal] = useState(false);
  const isFileManagementMicroserviceEnabled = IsFileManagementMicroserviceEnabled.toLowerCase();
  const arePairSelected = !selectedFiles.length || !selectedFolder.length || selectedFiles.length > REACT_APP_IManage_FileCount_Limit;

  const handleClose = () => {
    setOpen(false);
    setShowAlert(false);
  };

  const handleCloseErrorDialog = () => {
    setIsUnprocessableDialogOpen(false);
    if (unprocessableDialogCloseTimerRef.current) {
      clearTimeout(unprocessableDialogCloseTimerRef.current);
    }
    unprocessableDialogCloseTimerRef.current = setTimeout(() => {
      setUnprocessableErrorMessage("");
      unprocessableDialogCloseTimerRef.current = null;
    }, 250);
  };

  const openUnprocessableErrorDialog = (message: string) => {
    if (unprocessableDialogCloseTimerRef.current) {
      clearTimeout(unprocessableDialogCloseTimerRef.current);
      unprocessableDialogCloseTimerRef.current = null;
    }
    setUnprocessableErrorMessage(message);
    setIsUnprocessableDialogOpen(true);
  };

  const handleContinue = () => {
    setOpen(false);
    unselectAll();
    swapSource();
  };

  const onItemCollapsed = (e: ItemClickEvent<any>) => {
    showMoreLabel(e);
    e.component.beginUpdate();
    const collapse = (current: any) => {
      if (current.expanded) {
        e.component.collapseItem(current.uniqueId);
      }
      for (var i = 0; i < current.items.length; i++) {
        collapse(current.items[i]);
      }
    };
    if (e.itemData!.items) {
      e.itemData!.items.forEach((node) => collapse(node));
    }
    e.component.endUpdate();
  };

  const previousSelectedFileKeysRef = useRef<any[]>([]);
  const previousSelectedNodeKeysRef = useRef<any[]>([]);
  const normalizingSelectionRef = useRef(false);

  const getDescendantFiles = (node: Node<any>): Node<any>[] => {
    if (node.itemData?.itemType === "file") return [node];
    return (node.children ?? []).flatMap(getDescendantFiles);
  };

  const handleChangedFiles = (e: SelectionChangedEvent<any>) => {
    if (normalizingSelectionRef.current) return;

    const selectedNodes = e.component.getSelectedNodes();
    const previousNodeKeys = new Set(previousSelectedNodeKeysRef.current);
    const manuallySelectedFolders = selectedNodes.filter(
      (node: Node<any>) =>
        node.itemData?.itemType === "folder" && !previousNodeKeys.has(node.key)
    );
    const files = Array.from(
      new Map(
        [
          ...selectedNodes.filter((node: Node<any>) => node.itemData?.itemType === "file"),
          ...manuallySelectedFolders.flatMap(getDescendantFiles),
        ].map((file) => [file.key, file])
      ).values()
    );
    const retainedFiles = files.slice(0, REACT_APP_IManage_FileCount_Limit);

    if (files.length > REACT_APP_IManage_FileCount_Limit) {
      setShowAlert(true);
    }

    const folders = new Map<any, Node<any>>();
    retainedFiles.forEach((file) => {
      let parent = file.parent;
      while (parent) {
        folders.set(parent.key, parent);
        parent = parent.parent;
      }
    });

    normalizingSelectionRef.current = true;
    e.component.beginUpdate();
    e.component.unselectAll();
    folders.forEach((folder) => e.component.selectItem(folder.key));
    retainedFiles.forEach((file) => e.component.selectItem(file.key));
    e.component.endUpdate();
    normalizingSelectionRef.current = false;

    previousSelectedFileKeysRef.current = retainedFiles.map((file) => file.key);
    const selectedNodeKeys: any[] = [];
    folders.forEach((folder) => selectedNodeKeys.push(folder.key));
    previousSelectedNodeKeysRef.current = selectedNodeKeys.concat(
      retainedFiles.map((file) => file.key)
    );
    setSelectedFiles(retainedFiles);
    setSelectedFoldersIManage(retainedFiles);
  };

  const handleChangedFolder = (e: SelectionChangedEvent<any>) => {
    setSelectedFolder(e.component.getSelectedNodes());
  };

  const filesDuplicated = useCallback(
    (isFileDuplicated: boolean): MoveIManageFiles[] =>
      filesInspection?.all.filter((file) => file?.isDuplicated === isFileDuplicated),
    [filesInspection.all]
  );

  const fetchAppNameAndUpdateProps = async () => {
    try {
      const response = await taxPortalClientApi.taxPortal_GetAppName(siteID);

      let appName = response?.replace(/^"|"$/g, "").trim();

      if (!appName) return;

      switch (appName) {
        case "CS":
          setIsCorporateSight(true);
          break;
        case "TP":
          setIsCorporateSight(false);
          break;
        default:
          console.warn(`Unexpected appName received: "${appName}"`);
          break;
      }
    } catch (error) {
      console.error("Error fetching app name:", error);
    }
  };
   
  const scrollAndHighlightNode = (entityKey: string) => {
    if (!iManageTreeRef.current) return;

    const treeInstance = iManageTreeRef.current.instance;
    treeInstance.scrollToItem(entityKey).then(() => treeInstance.expandItem(entityKey)).then(() => {
      const treeEl = treeInstance.element();
      const nodeEl = treeEl.querySelector<HTMLElement>(`[data-item-id="${CSS.escape(encodeURI(entityKey))}"]`);

      if (nodeEl) {
        nodeEl.scrollIntoView({ behavior: "smooth", block: "center" });
        nodeEl.classList.remove("imanage-dropdown-highlight");
        void nodeEl.offsetWidth;
        nodeEl.classList.add("imanage-dropdown-highlight");
        setTimeout(() => nodeEl.classList.remove("imanage-dropdown-highlight"), 2000);
      }
    });
  };

  useEffect(() => {
    if (selectedEntity) {
      scrollAndHighlightNode(selectedEntity.uniqueId);
    }
  }, [selectedEntity, filesToMove.data]);

  const handleEntitySelect = (_: React.SyntheticEvent, entity: IManageEntityItem | null) => {
    setSelectedEntity(entity);
    if (iManageTreeRef.current) {
      const treeInstance = iManageTreeRef.current.instance;
      if (!treeInstance || typeof treeInstance.option !== "function") {
        return;
      }
      const currentSearchValue = treeInstance.option("searchValue");
      if (currentSearchValue) {
        treeInstance.option("searchValue", "");
      }
    }
  };

  const collectEntitiesFromData = (data: IManageFilesDTO[], targetId: string): IManageEntityItem | null => {
    const traverse = (items: IManageFilesDTO[]): IManageEntityItem | null => {
      for (const node of items) {
        if (node.level === 2 && node.id === targetId && node.uniqueId && node.text) {
          return { id: node.id, uniqueId: node.uniqueId, text: node.text };
        }
        if (node.items?.length) {
          const found = traverse(node.items);
          if (found) return found;
        }
      }
      return null;
    };
    return traverse(data);
  };

  const displayedEntity = expandedWorkspaceEntityId
    ? (collectEntitiesFromData(filesToMove.data, expandedWorkspaceEntityId) ?? selectedEntity)
    : selectedEntity;

  useEffect(() => {
    fetchAppNameAndUpdateProps();
  }, [siteID]);

  useEffect(() => {
    return () => {
      if (unprocessableDialogCloseTimerRef.current) {
        clearTimeout(unprocessableDialogCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!expandedWorkspaceEntityId) return;
    const entity = collectEntitiesFromData(filesToMove.data, expandedWorkspaceEntityId);
    setSelectedEntity(entity);
    if (entity) {
      scrollAndHighlightNode(entity.uniqueId);
    }
    onWorkspaceExpanded(null);
  }, [expandedWorkspaceEntityId]);


  useEffect(() => {
    if (!expandedWorkspaceEntityId) {
      setSelectedEntity(null);
    }
  }, [filesToMove.fulfilled]);


  useEffect(() => {
    if (!filesToMove.fulfilled || !filesToMove.data.length || !iManageTreeRef.current) return;

    const treeInstance = iManageTreeRef.current.instance;
    if (!treeInstance || typeof treeInstance.expandItem !== "function" || typeof treeInstance.option !== "function") return;

    const activeSearchValue = treeInstance.option("searchValue");
    if (activeSearchValue) return;

    filesToMove.data.forEach((rootNode) => {
      if (rootNode.uniqueId) {
        try {
          treeInstance.expandItem(rootNode.uniqueId);
        } catch {
          // Ignore expansion failures for transient nodes during refresh/swap.
        }
      }
    });
  }, [filesToMove.fulfilled, filesToMove.data]);


  const getLimitMessage = (message: string) => {
    return message.replace("{xx}", REACT_APP_IManage_FileCount_Limit.toString());
  };


  const moveFilesService = useCallback(
    async (data: MoveIManageFiles[]) => {
      const filesToMoveCleaned = data.map(({ isEdited, isOmitted, ...rest }) => rest);
      const moveFilesRequest = {
        data: filesToMoveCleaned,
        siteId: siteID,
        userEmail: user?.profile.preferred_username,
      };
      showSnackbar({ message: SnackbarMessage.MOVE_IMANAGE_FILES });
      try {
        setIsDisplayOverlay(true);
        const response = isFileManagementMicroserviceEnabled === "true"
          ? await fileManagementClientApi.imanageToHighq(moveFilesRequest, abortController.signal)
          : await taxPortalClientApi.taxPortal_MoveIManageFilesToTaxPortal(
            moveFilesRequest,
            iManageSource,
            abortController.signal
          );
        const { message, success } = response;
        let snackbarMessage = message;
        if (message?.toUpperCase() === "OK") {
          snackbarMessage = "Request is initiated and run in background";
        }
        unselectAll();
        showSnackbar({ message: snackbarMessage ?? "", severity: success ? "success" : "info" });
      } catch (error: any) {
        setIsDisplayOverlay(true);
        if (
          error.status === IManageFilesVars.CONFLICT_ERROR ||
          error.statusCode === IManageFilesVars.CONFLICT_ERROR
        ) {

          const conflictResponse = error?.data
            ? error.data
            : error.response ?? error;
          showSnackbar({
            message: conflictResponse?.message ?? "There are some conflicts with selected files",
            severity: "error",
          });

          setFilesInspection({
            ...filesInspection,
            all: conflictResponse?.filesForInspection ?? [],
          });

          setIsDisplayModal(true);
        }
        else if (error.status === IManageFilesVars.Unprocessable_ENTITY) {
          let errorMessage = SnackbarMessage.ERROR as string;
          try {
            const response = JSON.parse(error?.response ?? "{}");
            errorMessage = response.message ?? SnackbarMessage.ERROR;
          } catch {
            errorMessage = error?.response ?? SnackbarMessage.ERROR;
          }
          showSnackbar({ open: false, message: SnackbarMessage.MOVE_IMANAGE_FILES });
          openUnprocessableErrorDialog(errorMessage);
        }
        else {
          showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
      } finally {
        setIsDisplayOverlay(false);
      }
    },
    [filesInspection, siteID, iManageSource]
  );

  const moveFilesFromHighQtoIManage = useCallback(
    async (data: MoveHighQFiles[]) => {
      const rootFolderId = foldersToShow.data.find((item) => item.id !== undefined && item.uniqueId !== undefined && item.uniqueId.startsWith("0"))?.id;
      const extractMdmclientId = data.find((item) => item.mdmClientId !== undefined)?.mdmClientId;
      const extractWorkspaceName = data.find((item) => item.workspaceName !== undefined)?.workspaceName;

      const filesToMoveCleaned = data.map(({ isEdited, isOmitted, ...rest }) => rest);
      const moveFilesRequest = {
        data: filesToMoveCleaned,
        siteId: siteID,
        userEmail: user?.profile.preferred_username,
        mdmClientId: extractMdmclientId,
        workspaceName: extractWorkspaceName,
        hqRootFolderId: rootFolderId,
      };
      showSnackbar({ message: SnackbarMessage.MOVE_TAXPORTAL_FILES });
      try {
        setIsDisplayOverlay(true);
        if (isFileManagementMicroserviceEnabled === "true") {
          await fileManagementClientApi.highqToImanage(moveFilesRequest, abortController.signal);
        } else {
          const { mdmClientId, workspaceName, hqRootFolderId, ...rest } = moveFilesRequest;
          await taxPortalClientApi.taxPortal_MoveTaxPortalFilesToIManage(
            rest,
            abortController.signal
          );
        }
        unselectAll();
      } catch (error: any) {
        setIsDisplayOverlay(true);
        showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
      } finally {
        setIsDisplayOverlay(false);
      }
    },
    [siteID, iManageSource]
  );

  const unselectAll = () => {
    previousSelectedFileKeysRef.current = [];
    previousSelectedNodeKeysRef.current = [];
    setSelectedFiles([]);
    setSelectedFoldersIManage([]);
    setSelectedFolder([]);
    iManageTreeRef.current!.instance.unselectAll();
    taxPortalTreeRef.current!.instance.unselectAll();
  };

  const handleCopyRequest = async () => {
    if (selectedFiles.length > REACT_APP_IManage_FileCount_Limit) {
      setShowAlert(true);
    } else {
      setShowAlert(false);
      if (iManageSource) {
        const data = selectedFiles.map((item: Node<any>): MoveIManageFiles => ({
          sourceDocumentId: item.itemData!.id as string,
          sourceContainerId: item.parent!.itemData!.id as string,
          targetParentFolderId: selectedFolder[0].itemData!.id as string,
          fileName: item.itemData!.text as string,
          documentId: 0,
        }));
        moveFilesService(data);
      } else {
        const data = selectedFiles.map((item: Node<any>): MoveHighQFiles => {
          const fullFilePath = getFilePath(item.itemData!, foldersToShow.data);
          const hqEntity = findHighQEntity(item.itemData!, foldersToShow.data);
          const hqEntityId = hqEntity?.id ? parseInt(hqEntity.id, 10) : 0;
          const { id: workspaceName, path: destinationFolderPath } = getFolderPath(selectedFolder[0]);

          return {
            siteId: siteID,
            hQFileId: item.itemData!.id as string,
            hQItemId: item.itemData!.hqItemId as string,
            fileName: item.itemData!.text as string,
            workspaceName,
            hQEntityID: hqEntityId,
            hQParentFolderId: (item.parent!.itemData!.id as string) || "",
            hQParentFolderPath: fullFilePath,
            hQEntityName: hqEntity?.text as string,
            destinationFolderPath,
          };
        });
        moveFilesFromHighQtoIManage(data);
      }
    }
  };

  const handleCloseModal = () => {
    setItemEdited([]);
    setIsDisplayModal(false);
    setIsBlurEnabled(false);
  };

  const renderChecksEnabledOnFilesOnly = (e: any) => {
    const checkBoxEl = e?.itemElement?.parentElement?.querySelector(".dx-checkbox");
    if (!checkBoxEl) return;

    const checkBox = CheckBox.getInstance(checkBoxEl);
    if (!checkBox || typeof checkBox.option !== "function") return;

    checkBox.option("iconSize", 14);

    if (e.itemData!.itemType === "folder") {
      checkBox.option("disabled", true);
      checkBoxEl.style.background = "#ddd";
    }
  };

  const renderRadioButtons = (e: any) => {
    const checkBoxEl = e?.itemElement?.parentElement?.querySelector(".dx-checkbox");
    if (!checkBoxEl) return;

    const checkBox = CheckBox.getInstance(checkBoxEl);
    if (!checkBox || typeof checkBox.option !== "function") return;

    const isLevelAllowed = e.itemData.level >= 1 && e.itemData.level <= 3;
    const shouldDisable = !iManageSource && isLevelAllowed;

    if (shouldDisable) {
      e.itemElement.style.pointerEvents = "none";

      const toggleButton = e.itemElement.querySelector(".dx-treeview-toggle-item-visibility");
      if (toggleButton) {
        toggleButton.style.pointerEvents = "auto";
        toggleButton.style.opacity = "1";
      }
      checkBox.option("disabled", true);
    }

    if (!shouldDisable) {
      let radioGroupEl = e.itemElement.parentElement.querySelector(".imanage-radio-host") as HTMLElement | null;
      if (!radioGroupEl) {
        radioGroupEl = document.createElement("DIV");
        radioGroupEl.className = "imanage-radio-host";
        e.itemElement.parentElement.prepend(radioGroupEl);
      }

      const radioGroup = new RadioGroup(radioGroupEl, {
        items: [{ value: true }],
        valueExpr: "value",
        value: checkBox.option("value"),
        onValueChanged(args: any) {
          checkBox.option("value", args.value);
        },
      });

      const defaultHandler: any = checkBox.option("onValueChanged");
      checkBox.option("onValueChanged", (args: any) => {
        radioGroup.option("value", args.value);
        if (typeof defaultHandler === "function") {
          defaultHandler(args);
        }
      });
    }

    checkBox.option("visible", false);
  };

  const renderTreeViewItem = (item: any) => {
    if (item.text === "Show More" || item.text === "Hide") {
      return (
        <span>
          <b>{item.text}</b>
        </span>
      );
    } else {
      return (
        <>
          <i className={`dx-icon dx-icon-${item.icon}`}></i>
          <span>{item.text}</span>
        </>
      );
    }
  };

  iManageProps.noDataText = filesToMove.pending ? "Loading data..." : "No data to display";
  iManageProps.items = filesToMove.data;
  iManageProps.keyExpr = "uniqueId";
  iManageProps.selectedCounter = String(selectedFiles.length);
  iManageProps.onSelectionChanged = isSwap ? handleChangedFolder : handleChangedFiles;
  iManageProps.selectionMode = isSwap ? "single" : "multiple";
  iManageProps.onItemRendered = isSwap ? renderRadioButtons : renderChecksEnabledOnFilesOnly;
  iManageProps.onItemExpanded = onItemExpanded;
  iManageProps.onItemCollapsed = onItemCollapsed;
  iManageProps.itemRender = renderTreeViewItem;
  iManageProps.handleRefreshClick = () => handleRefreshClick(IMANAGE_FILES);
  iManageProps.refreshing = isRefreshingImanage;
  iManageProps.width = width - 10;
  iManageProps.headerSlot = (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Autocomplete
        size="small"
        options={entities}
        getOptionLabel={(option) => option.text}
        isOptionEqualToValue={(option, value) => option.uniqueId === value.uniqueId}
        value={displayedEntity}
        onChange={handleEntitySelect}
        disabled={!entities.length}
        sx={{ width: 200, ml: 1 }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Jump to Entity"
            inputProps={{ ...params.inputProps, style: { fontSize: "13px" } }}
          />
        )}
        ListboxProps={{ style: { maxHeight: 300, fontSize: "14px" } }}
      />
      <Tooltip
        title="Select an entity from the dropdown to navigate directly to it. To search for files, expand a workspace folder first."
        placement="top"
        arrow
      >
        <InfoOutlinedIcon
          sx={{ ml: 1, fontSize: 20, color: "action.active", cursor: "pointer" }}
        />
      </Tooltip>
    </Box>
  );

  iManageProps.filesLimit = REACT_APP_IManage_FileCount_Limit;


  taxPortalProps.noDataText = foldersToShow.pending ? "Loading data..." : "No data to display";
  taxPortalProps.items = foldersToShow.data;
  taxPortalProps.keyExpr = "uniqueId";
  taxPortalProps.onSelectionChanged = isSwap ? handleChangedFiles : handleChangedFolder;
  taxPortalProps.onItemRendered = isSwap ? renderChecksEnabledOnFilesOnly : renderRadioButtons;
  taxPortalProps.selectionMode = isSwap ? "multiple" : "single";
  taxPortalProps.onItemCollapsed = onItemCollapsed;
  taxPortalProps.handleRefreshClick = () => handleRefreshClick(TAXPORTAL_FOLDERS);
  taxPortalProps.refreshing = isRefreshingTaxPortal;
  taxPortalProps.width = width - 10;
  taxPortalProps.filesLimit = REACT_APP_IManage_FileCount_Limit;

  taxPortalProps.title = isCorporateSight ? CS_FOLDERS : TAXPORTAL_FOLDERS;
  taxPortalProps.id = isCorporateSight ? CORPORATESIGHT_ID : TAXPORTAL_ID;

  const swapSource = () => {
    setisSwap((prev) => !prev);
  };

  const clearTreeSearchValues = () => {
    const iManageTree = iManageTreeRef.current?.instance;
    if (iManageTree && typeof iManageTree.option === "function") {
      const searchValue = iManageTree.option("searchValue");
      if (searchValue) {
        iManageTree.option("searchValue", "");
      }
    }

    const taxPortalTree = taxPortalTreeRef.current?.instance;
    if (taxPortalTree && typeof taxPortalTree.option === "function") {
      const searchValue = taxPortalTree.option("searchValue");
      if (searchValue) {
        taxPortalTree.option("searchValue", "");
      }
    }
  };

  const handleSwapClick = async () => {
    if (isOneSelected) {
      setOpen(true);
      return;
    }

    clearTreeSearchValues();
    swapSource();
  };

  useEffect(() => {
    if (iManageProps.onItemRendered === renderChecksEnabledOnFilesOnly) {
      onSourceBoolChange(true, false);
      setiManageSource(true);
    } else {
      onSourceBoolChange(false, true);
      setiManageSource(false);
    }
  }, [isSwap]);

  useEffect(() => {
    setFilesInspection({
      all: filesInspection.all,
      duplicated: filesDuplicated(true),
      copy: filesDuplicated(false),
      initial: filesDuplicated(true),
    });
  }, [filesDuplicated]);

  const props = {
    filesInspection,
    isDisplayModal,
    setFilesInspection,
    setIsDisplayModal,
    itemEdited,
    setItemEdited,
    onHandleService: iManageSource ? moveFilesService : moveFilesFromHighQtoIManage,
    handleCloseModal,
    isBlurEnabled,
    setIsBlurEnabled,
  };

  const handleRefreshClick = async (target?: string) => {
    const isTaxPortal = target === TAXPORTAL_FOLDERS;

    const startMessage = isTaxPortal
      ? isCorporateSight ? SnackbarMessage.CORPOATESITE_FOLDER_REFRESH : SnackbarMessage.TAXPORTAL_FOLDER_REFRESH
      : SnackbarMessage.IMANAGE_WORKSPACES_REFRESH;

    const successMessage = isTaxPortal
      ? isCorporateSight ? SnackbarMessage.CORPOATESITE_FOLDER_REFRESH_SUCCESS : SnackbarMessage.TAXPORTAL_FOLDER_REFRESH_SUCCESS
      : SnackbarMessage.IMANAGE_WORKSPACES_REFRESH_SUCCESS;

    const failMessage = isTaxPortal
      ? isCorporateSight ? SnackbarMessage.CORPOATESITE_FOLDER_REFRESH_FAIL : SnackbarMessage.TAXPORTAL_FOLDER_REFRESH_FAIL
      : SnackbarMessage.IMANAGE_WORKSPACES_REFRESH_FAIL;

    const setRefreshing = isTaxPortal ? setIsRefreshingTaxPortal : setIsRefreshingImanage;
    const finalRefreshTarget = isTaxPortal ? TAXPORTAL_FOLDERS : IMANAGE_FILES;

    try {
      refresh(null);
      showSnackbar({ message: startMessage });
      setRefreshing(true);
      await (
        isFileManagementMicroserviceEnabled === "true"
          ? createSitesClientApi.iManageWorkspace_RefreshEntityImanage(siteID, true, target)
          : taxPortalClientApi.hQFolder_RefreshEntityImanage(siteID, true, target)
      );
      
    } catch (error) {
      showSuccessSnackbar({ message: failMessage });
    } finally {
      showSuccessSnackbar({ message: successMessage });
      refresh(finalRefreshTarget);
      setRefreshing(false);
    }
  };

  return (
    <>
      <div>
        <Box
          sx={{
            paddingLeft: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingRight: 3,
          }}
        >
          <Typography variant="h6" component="h1" gutterBottom sx={{ fontSize: "15px !important", paddingTop: 2 }}>
            {iManageSource
              ? isCorporateSight
                ? getLimitMessage(IMANAGECSFILE_TITLE)
                : getLimitMessage(IMANAGEFILE_TITLE)
              : isCorporateSight
                ? getLimitMessage(IMANAGECSFILE_SWAP_TITLE)
                : getLimitMessage(IMANAGEFILE_SWAP_TITLE)}
          </Typography>
        </Box>
        <Divider sx={{ background: "#099cde" }} />
      </div>
      <div className="hq-page">
        <div className="hq-imanage-files">
          <div className="hq-tree">
            <Tree {...taxPortalProps} reference={taxPortalTreeRef} />
          </div>
          <Button variant="outlined" style={{ marginTop: '40px' }} className="button-centre" onClick={handleSwapClick}>
            {iManageSource ? (
              <ForwardIcon sx={{ color: "black", transform: "rotate(180deg)" }} />
            ) : (
              <ForwardIcon sx={{ color: "black" }} />
            )}
          </Button>
          <div className="hq-tree" ref={componentRef}>
            <Tree {...iManageProps} reference={iManageTreeRef} />
          </div>
        </div>
        <Button
          onClick={handleCopyRequest}
          disabled={arePairSelected}
          variant="outlined"
          className="hq_button button-right"
        >
          Copy
        </Button>
        <Button onClick={unselectAll} disabled={!isOneSelected} variant="outlined" className="hq_button button-right">
          Clear selection
        </Button>
      </div>
      <ModalCopyFiles {...props} />
      <Backdrop open={isDisplayOverlay}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogBox
        maxWidth={"xs"}
        title={'Alert'}
        btnOpenText={'Ok'}
        isDisplayModal={showAlert}
        handleContinue={handleClose}
        btnCloseText={''}
        icon={<InfoOutlinedIcon sx={{ color: "#009CDE", width: 22, height: 22 }} />}
        dialogContentSx={{ borderTop: "1px solid #e0e0e0" }}
        handleClose={handleClose}
        dialogTitleSx={{ color: '#515356', display: "flex", alignItems: "center", gap: "8px", }}
      >
        <div style={{ margin: "14px 0px" }}>
          <span style={{ fontSize: "16px" }}>
            {getLimitMessage(FILES_LIMIT_PLACEHOLDER)}
          </span>
        </div>
      </DialogBox>
      <DialogBox
        maxWidth={"xs"}
        title={'Alert'}
        btnOpenText={'Ok'}
        isDisplayModal={isUnprocessableDialogOpen}
        handleContinue={handleCloseErrorDialog}
        btnCloseText={''}
        icon={<InfoOutlinedIcon sx={{ color: "#009CDE", width: 22, height: 22 }} />}
        dialogContentSx={{ borderTop: "1px solid #e0e0e0" }}
        handleClose={handleCloseErrorDialog}
        dialogTitleSx={{ color: '#515356', display: "flex", alignItems: "center", gap: "8px", }}
      >
        <div style={{ margin: "14px 0px" }}>
          <span style={{ fontSize: "16px" }}>
            {unprocessableErrorMessage}
          </span>
        </div>
      </DialogBox>
      <IManageFilesDialog open={open} handleClose={handleClose} handleContinue={handleContinue} />
    </>
  );
}
